import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type RewindStillMap,
  type RewindsStillStoryboard,
  stillsResolutionConfig,
  updateStillsMap,
} from 'utils/rewinds/previewStills';
import fetch from 'isomorphic-fetch';
import {
  ClipsPlaylistBlock,
  SmartRewindPlaylistBlock,
  TrimmingClipPlaylistBlock,
} from 'utils/rewinds/playlistBlocks.types';
import type { PlayerStills, RewindRecording } from 'types/recordings';

const useStills = ({
  currentPlaylist,
  disableStills,
  isBuffering,
  isScrubbing,
  sliderTimeValue,
  fetchStillsCallback,
}: {
  currentPlaylist:
    | SmartRewindPlaylistBlock
    | ClipsPlaylistBlock
    | TrimmingClipPlaylistBlock
    | undefined;
  disableStills?: boolean;
  isBuffering?: boolean;
  isScrubbing?: boolean;
  sliderTimeValue: number | null;
  fetchStillsCallback?: (stillsMap: RewindStillMap) => void;
}) => {
  const [stills, setStills] = useState<RewindStillMap | undefined>(undefined);
  const [stillsLoading, setStillsLoading] = useState<boolean>(true);

  const currentPreviewImage = useMemo(() => {
    const key = sliderTimeValue
      ? new Date(Math.round(sliderTimeValue / 1000) * 1000).toISOString()
      : null;
    return key ? stills?.get(key) : undefined;
  }, [sliderTimeValue, stills]);

  const showPreviewImage =
    (isBuffering || isScrubbing) &&
    !!currentPreviewImage &&
    !!currentPlaylist &&
    !!sliderTimeValue &&
    sliderTimeValue <= currentPlaylist.bucketEnd.getTime();

  const preloadLowQualityStoryboardImages = useCallback(
    (baseUrl: string, storyboards: PlayerStills['storyboards']) => {
      if (baseUrl && storyboards?.length > 0 && storyboards[0]?.images?.length > 0) {
        storyboards[0].images.forEach((url: string) => {
          const img = new Image();
          img.src = `${baseUrl}${url}`;
        });
      }
    },
    [],
  );

  const preloadPOCStoryboardImages = useCallback((stillsResponses: PlayerStills[]) => {
    stillsResolutionConfig?.preloadResolutions.forEach((quality) => {
      stillsResponses.forEach((stillsResponse: PlayerStills) => {
        const storyboards = stillsResponse.storyboards_sized;
        if (!storyboards?.[quality]?.images?.length) return;
        const { baseUrl } = stillsResponse;
        if (baseUrl && storyboards?.[quality]?.images?.length > 0) {
          storyboards[quality].images.forEach((url: string) => {
            const img = new Image();
            img.src = `${baseUrl}${url}`;
          });
        }
      });
    });
  }, []);

  const fetchStills = useCallback(async () => {
    setStillsLoading(true);
    const rewinds = currentPlaylist?.rewinds || [];
    const stillsResponses = await Promise.all(
      rewinds.map(async (rewind) => {
        const { playerStillsManifest } = rewind as RewindRecording;
        if (!playerStillsManifest) return {};
        try {
          const response = await fetch(playerStillsManifest);
          return await response.json();
        } catch (error) {
          console.error('Error fetching stills manifest', error);
          return {};
        }
      }),
    );

    let newStillsMap = new Map<
      string,
      {
        storyboards: Array<RewindsStillStoryboard> | undefined;
      }
    >();

    stillsResponses.forEach((stillsResponse: PlayerStills, index) => {
      const rewind = rewinds[index];
      newStillsMap = updateStillsMap(
        newStillsMap,
        rewind.startDate,
        rewind.endDate,
        stillsResponse,
      );

      if (!stillsResolutionConfig.preloadResolutions) {
        preloadLowQualityStoryboardImages(stillsResponse?.baseUrl, stillsResponse?.storyboards);
      }
    });

    if (stillsResolutionConfig.preloadResolutions) {
      preloadPOCStoryboardImages(stillsResponses);
    }

    setStills(newStillsMap);
    setStillsLoading(false);
    if (fetchStillsCallback) fetchStillsCallback(newStillsMap);
  }, [
    currentPlaylist?.rewinds,
    fetchStillsCallback,
    preloadLowQualityStoryboardImages,
    preloadPOCStoryboardImages,
  ]);

  useEffect(() => {
    if (disableStills) return;

    if (currentPlaylist && !(currentPlaylist as ClipsPlaylistBlock)?.stills) {
      setStills(undefined);
      fetchStills();
    } else if ((currentPlaylist as ClipsPlaylistBlock)?.stills) {
      setStillsLoading(false);
      setStills((currentPlaylist as ClipsPlaylistBlock)?.stills);
    }
  }, [currentPlaylist, disableStills, fetchStills]);

  return { currentPreviewImage, showPreviewImage, stills, stillsLoading };
};

export default useStills;
